import "./LocationsPage.scss";
import { useCallback, useEffect, useState } from "react";
import LocationsBanner from "../../LocationsBanner";
import TeamsApi from "../../../../api/TeamsApi";
import { useRecoilState } from "recoil";
import { locationsState, memberState } from "../../../../state/atoms";
import {
  AddOnDtoTypeEnum,
  LocationDto,
  TransferFilesDto,
} from "../../../../generated/account-client";
import LocationCard from "../../location/LocationCard";
import RemoveLocationModal from "../../modal/RemoveLocationModal";
import TransferFilesToLocationModal from "../../modal/TransferFilesToLocationModal";
import AccountLocationApi from "../../../../api/AccountLocationApi";
import { success } from "../../../../service/MessageService";
import AccountsApi from "../../../../api/AccountsApi";
import EditLocationWarningModal from "../../modal/EditLocationWarningModal";
import MainAppLocationIcon from "../../../icons/MainAppLocationIcon";
import { useNavigate } from "react-router-dom";
import { MemberDtoPlanEnum } from "../../../../generated/dashboard-client";
import { env } from "../../../../env/env";
import { Skeleton } from "antd";
import DSAApi from "../../../../api/DSAApi";
import LocationsApi from "../../../../api/LocationsApi";

export function LocationsPage() {
  const [storeLocations, setStoreLocation] = useRecoilState(locationsState);
  const [member] = useRecoilState(memberState);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState({} as LocationDto);
  const [locations, setLocations] = useState([] as LocationDto[]);
  const [primaryLocation, setPrimaryLocation] = useState({} as LocationDto);
  const [removeLocation, setRemoveLocation] = useState(false);
  const [transferFiles, setTransferFiles] = useState(false);
  const [editLocationWarningModal, setEditLocationWarningModal] = useState(
    {} as LocationDto
  );
  const [locationUserCounts, setLocationUserCounts] = useState<Record<number, number>>({});
  const [locationScreenCounts, setLocationScreenCounts] = useState<Record<number, number>>({});

  const fetchLocationCounts = useCallback(async (locationId: number, memberId: string) => {
    try {
      const [userCountPromise, userDetailsPromise, screenCount] = await Promise.all([
        LocationsApi.getLocationPermissions(locationId),
        AccountsApi.getUsersDetails(memberId),
        DSAApi.getScreensPerLocation(locationId).then(resp => resp.data.length)
      ]);

      const validUsers = userDetailsPromise.data.users?.map(it => it.id) || [];
      const userCount = userCountPromise.data.filter(it => validUsers.includes(it.memberId)).length;
      setLocationUserCounts(prev => ({...prev, [locationId]: userCount}));
      setLocationScreenCounts(prev => ({...prev, [locationId]: screenCount}));
    } catch (error) {
      console.error('Error fetching location counts:', error);
    }
  }, []);

  const fetchLocations = useCallback(() => {
    if (!member.teamId || !member.id) return;

    setLoading(true);
    TeamsApi.getTeamLocationsWithLogos(member.teamId)
      .then((resp) => {
        setLocations(resp.data);
        resp.data.forEach(location => {
          if (location.internalId) {
            fetchLocationCounts(location.internalId, member.id);
          }
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [member.teamId, member.id, fetchLocationCounts]);

  const fetchPrimaryLocation = useCallback(() => {
    if (!member.internalId) return;
    AccountsApi.getPrimaryUserLocation(member.internalId)
      .then((resp: any) => {
        setPrimaryLocation(resp.data);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [member.internalId]);

  useEffect(fetchLocations, [fetchLocations]);
  useEffect(fetchPrimaryLocation, [fetchPrimaryLocation]);

  function addNewLocation() {
    if (member.plan === MemberDtoPlanEnum.Free) window.MHM.ProModal.upgradeWithLocations({memberId: member.id, onSuccess: () => window.location.reload()});
    else window.MHM.ProModal.addLocation({memberId: member.id, onSuccess: () => {
        fetchLocations();
      }});
  }

  function onEdit(location: LocationDto) {
    setEditLocationWarningModal(location);
  }

  function onEditLocationWarningContinue(location: LocationDto) {
    navigate(env.dashboardPath + `/account/locations/${location.id}`);
  }

  function onRemove(location: LocationDto) {
    setLocation(location);
    setRemoveLocation(true);
  }

  function onRemoveAndTransferFiles() {
    setRemoveLocation(false);
    setTransferFiles(true);
  }

  async function onRemoveAndDeleteFiles(location: LocationDto) {
    await AccountsApi.removeSubscriptionAddon(
      member.id,
      AddOnDtoTypeEnum.Location
    );
    await AccountLocationApi.deleteLocation(location.internalId || -1);

    onRemoveSuccess(location);
  }

  async function onTransfer(
    newLocation: LocationDto,
    currentLocation: LocationDto
  ) {
    const transferFilesDto = {
      fromLocationId: currentLocation.internalId || location.internalId,
      toLocationId: newLocation.internalId,
    } as TransferFilesDto;

    await AccountsApi.removeSubscriptionAddon(
      member.id,
      AddOnDtoTypeEnum.Location
    );
    await AccountLocationApi.transferTimberDesign(transferFilesDto);

    setTransferFiles(false);
    onRemoveSuccess(currentLocation);
  }

  function onRemoveSuccess(location: LocationDto) {
    success("Location removed!");
    setRemoveLocation(false);

    fetchLocations();
    fetchPrimaryLocation();

    setStoreLocation(
      storeLocations.filter((it) => it.internalId !== location.internalId)
    );
  }

  return (
    <div className={"content locations-page"}>
      <LocationsBanner />

      {loading && <Skeleton />}

      {!loading && (
        <>
          <section className={"locations"}>
            <div className={"add-new align-center"} onClick={addNewLocation}>
              <MainAppLocationIcon width={60} height={60} color={""} />
              <br />
              <h4>
                Add <br /> Location
              </h4>
            </div>

            {locations.map((it) => (
              <LocationCard
                disableRemove={locations.length === 1}
                key={it.id}
                location={it}
                managersCount={((locationUserCounts[it.internalId!!] || 0) + 1)}
                primaryLocation={primaryLocation}
                onEdit={onEdit}
                onSetPrimary={(location) => setPrimaryLocation(location)}
                onRemove={onRemove}
              />
            ))}
          </section>
        </>
      )}

      <RemoveLocationModal
        location={location}
        visible={removeLocation}
        onCancel={() => setRemoveLocation(false)}
        onDelete={onRemoveAndDeleteFiles}
        onRemoveAndTransferFiles={onRemoveAndTransferFiles}
        userCount={locationUserCounts[location.internalId!!]}
        screenCount={locationScreenCounts[location.internalId || -1] || 0}
      />
      <TransferFilesToLocationModal
        visible={transferFiles}
        location={location}
        locations={locations.filter(
          (loc) => loc.internalId !== location.internalId
        )}
        onCancel={() => setTransferFiles(false)}
        onTransfer={onTransfer}
      />
      <EditLocationWarningModal
        visible={!!editLocationWarningModal.id}
        location={editLocationWarningModal}
        onCancel={() => setEditLocationWarningModal({})}
        onContinue={onEditLocationWarningContinue}
      />
    </div>
  );
}
